import { useEffect, useState, useRef } from 'react';
import type { UnlinkUserParams, UserInfo } from './interface';
import type { User, UserList } from 'types/UserZendesk';
import {
  getCurrentTicketNumber,
  getCustomObjects,
  getMyUser,
  zafClient,
  createCustomObject,
  searchCustomObjectByName,
  getFieldNames,
  popupError,
} from '@services/Zendesk';
import { useUserContext } from '@context/UserContext';
import { useCustomObjectFields } from '@hooks/useCustomObjectsFields';

export function useHome() {
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [trueRoles, setTrueRoles] = useState<Record<number, string>>({});
  const [problemMessage, setProblemMessage] = useState<string>('');
  const hasInitializedRef = useRef(false);
  const {
    usersInfo,
    setUsersInfo,
    userIdRoles,
    setUserIdRoles,
    urlCustom,
    setUrlCustom,
    setSelectedUser,
    setSelectedRole,
    setUserRoles,
  } = useUserContext();
  const { availableRoles } = useCustomObjectFields();
  const [loadingCreateObject, setLoadingCreateObject] = useState(false);

  const openModal = (user: User) => {
    setUserToDelete(user);
    setVisible(true);
  };
  const onClose = () => {
    setVisible(false);
    setUserToDelete(null);
  };

useEffect(() => {
  if (hasInitializedRef.current) return;
  hasInitializedRef.current = true;
  
  (async () => {
    try {
      setLoadingCreateObject(true);
      const ticket = await getCurrentTicketNumber(zafClient);
      
      const existingCustomObjects = await getCustomObjects(
        zafClient,
        ticket as number,
      );
      
      if (existingCustomObjects && existingCustomObjects.length > 0) {
        const urls = existingCustomObjects.map((item: { url: string }) => item.url);
        setUrlCustom(urls);
        setLoadingCreateObject(false);
        return;
      }
      
      const accountName = `Conta do ticket ${ticket}`;
      const field = await getFieldNames(zafClient);
      const fieldContaString = field?.id?.toString();

      let existingObject = await searchCustomObjectByName(
        zafClient,
        accountName
      );

      if (!existingObject?.custom_object_records?.length) {
        await createCustomObject(
          zafClient,
          ticket as number,
          accountName
        );

        existingObject = await searchCustomObjectByName(
          zafClient,
          accountName
        );
      }

      if (existingObject?.custom_object_records?.length) {
        const objectId = existingObject.custom_object_records[0].id;

        await zafClient.request({
          url: `/api/v2/tickets/${ticket}.json`,
          type: "PUT",
          dataType: "json",
          contentType: "application/json",
          data: JSON.stringify({
            ticket: {
              custom_fields: {
                ...(fieldContaString && { [fieldContaString]: objectId }),
              },
            },
          }),
        });

        const response = await getCustomObjects(
          zafClient,
          ticket as number
        );

        const urls = response.map((item: { url: string }) => item.url);
        setUrlCustom(urls);
      }
    } catch (error: unknown) {
      console.error("Erro completo:", error);
      
      const err = error as { 
        responseJSON?: { 
          description?: string; 
          details?: { 
            base?: Array<{
              description?: string;
              error?: string | null;
              ticket_field_id?: number;
              ticket_field_type?: string;
            }>;
          };
        };
      };
      const message =
      err?.responseJSON?.details?.base?.[0]?.description ||
      err?.responseJSON?.description ||
      "Verifique os campos obrigatórios no ticket";
      setProblemMessage(message);
      popupError(`${message}`, true);
      setLoadingCreateObject(false);
      setLoading(false);

    } finally {
      setLoadingCreateObject(false);
    }
  })();
}, []);

  useEffect(() => {
    async function fetchCustomFields() {
      setLoading(true);
      try {
        const user = await getMyUser(zafClient) as { user?: { role?: string } };
        setUserRoles(user?.user?.role || '');
        const allFields = await Promise.all(
          urlCustom.map(async (url: string) => {
            const response = await zafClient.request({
              url,
              type: 'GET',
            });
            const customObjectRecord = response as UnlinkUserParams;
            return {
              url,
              fields: customObjectRecord.custom_object_record.custom_object_fields,
            };
          })
        );

        const allLinks = allFields.flatMap((obj) =>
          Object.entries(obj.fields)
            .filter(([, userId]) => userId)
            .map(([role, userId]) => ({
              id: Number(userId),
              role,
              userId: Number(userId),
              objectUrl: obj.url,
            }))
        );

        setUserIdRoles(allLinks);

        const uniqueUserIds = [...new Set(allLinks.map((link) => link.userId))];
        const users = await Promise.all(
          uniqueUserIds.map((id) =>
            zafClient
              .request({
                url: `/api/v2/users/${id}`,
                type: 'GET',
              })
              .then((res) => {
                const typedRes = res as UserList;
                return typedRes.user;
              })
          )
        );
        const roleMap: Record<number, string> = {};
        users.forEach((user) => {
          roleMap[user.id as number] = user.role;
        });
        setTrueRoles(roleMap)

        const usersWithRoles = allLinks.map((link) => {
          const user = users.find((u: User) => u.id === link.userId);
          return {
            ...user,
            role: link.role,
            objectUrl: link.objectUrl,
          };
        });

        setUsersInfo(usersWithRoles as UserInfo[]);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
      } finally {
        setLoading(false);
      }
    }

    if (urlCustom.length > 0) {
      fetchCustomFields();
    }
  }, [urlCustom]);

  const handleUnlink = async (user: UserInfo) => {
    if (!user.objectUrl) return;
    try {
      setLoading(true);
      const response = await zafClient.request({
        url: user.objectUrl,
        type: 'GET',
      });

      const customObjectResponse = response as UnlinkUserParams;
      const updatedFields = {
        ...customObjectResponse.custom_object_record.custom_object_fields,
        [user.role]: '',
      };

      await zafClient.request({
        url: user.objectUrl,
        type: 'PUT',
        data: {
          custom_object_record: {
            custom_object_fields: updatedFields,
          },
        },
      });

      setUsersInfo(usersInfo.filter((u) => !(u.id === user.id && u.role === user.role)));
      onClose();
    } catch (error) {
      console.error('Error unlinking:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    urlCustom,
    userIdRoles,
    usersInfo,
    loading,
    setSelectedUser,
    openModal,
    onClose,
    visible,
    userToDelete,
    handleUnlinkUser: handleUnlink,
    availableRoles,
    setSelectedRole,
    trueRoles,
    loadingCreateObject,
    problemMessage
  };
}
